/**
 * The rooms in the host's colours: every token a guest page reads, worked out from the brand the host picked in
 * setup (the accent, the secondary, the page tint). A port of the confirmed prototype's `applyLook`
 * (g-front/index.html), so the rooms look as designed in every one of the six sets and in Custom.
 *
 * The rooms are dark: the ground is the brand's own near-black, the ink is its page tint. A named set carries its
 * own ink and dark; Custom derives them from the accent, as the prototype does.
 */
import { contrastRatio, DEFAULT_ACCENT, DEFAULT_SECONDARY, mixHex, normalHex } from "./brand-colors";

interface Set5 {
  a: string;
  b: string;
  t: string;
  i: string;
  d: string;
}

/** The six sets a host picks from in setup, as the prototype draws them. */
export const LOOK_SETS: Record<string, Set5> = {
  wine: { a: "#7a2338", b: "#b0873a", t: "#fbf6f1", i: "#2a1c20", d: "#170f12" },
  forest: { a: "#2f5a43", b: "#c8963e", t: "#f3f6f0", i: "#1c2a22", d: "#0f1612" },
  midnight: { a: "#22305a", b: "#8aa4e0", t: "#f2f4f9", i: "#1a2033", d: "#0e1220" },
  ocean: { a: "#0d5f78", b: "#e3a456", t: "#eff6f7", i: "#13303a", d: "#0b161a" },
  terracotta: { a: "#b4532f", b: "#3f5a48", t: "#fbf3ec", i: "#33201a", d: "#1a110d" },
  charcoal: { a: "#2e2e31", b: "#c49a2c", t: "#f5f4f1", i: "#1e1e20", d: "#121214" },
};

const WHITE = "#ffffff";
const BLACK = "#000000";

/** The colour moved step by step toward `goal` until `ok` holds (or it reaches it). */
function toward(color: string, goal: string, ok: (c: string) => boolean): string {
  let k = 0;
  let out = color;
  while (!ok(out) && k < 1) {
    k += 0.03;
    out = mixHex(color, goal, Math.min(1, k));
  }
  return out;
}

function rgb(hex: string): string {
  const h = normalHex(hex) ?? BLACK;
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)).join(",");
}

/** The five colours a look is drawn from: a named set when the accent is one of them, else derived. */
export function lookColours(brand: { accent?: string | null; brass?: string | null; paper?: string | null }): Set5 {
  const accent = normalHex(brand.accent) ?? DEFAULT_ACCENT;
  const named = Object.values(LOOK_SETS).find((s) => s.a === accent);
  const b = normalHex(brand.brass) ?? named?.b ?? DEFAULT_SECONDARY;
  const d = named?.d ?? mixHex(accent, BLACK, 0.87);
  const i = named?.i ?? mixHex(accent, BLACK, 0.74);
  // Why: the tint is the rooms' ink, on a near-black ground; a dark page tint from before the rooms (a dark brand
  // page) would write dark words on it, so the set's own tint, or the suite's, takes its place.
  const paper = normalHex(brand.paper);
  const readable = paper && contrastRatio(paper, mixHex(d, BLACK, 0.26)) >= 7 ? paper : null;
  return { a: accent, b, t: readable ?? named?.t ?? "#fbf6f1", i, d };
}

/** Every token the rooms read, as inline CSS variables for the `.gst` root. */
export function guestLook(brand: { accent?: string | null; brass?: string | null; paper?: string | null }): Record<string, string> {
  const C = lookColours(brand);
  const night = mixHex(C.d, BLACK, 0.26);
  const n2 = mixHex(mixHex(C.d, C.a, 0.06), WHITE, 0.022);
  const n3 = mixHex(mixHex(C.d, C.a, 0.12), WHITE, 0.05);
  const n4 = mixHex(mixHex(C.d, C.a, 0.16), WHITE, 0.09);
  // Why: a dark accent (Charcoal, Midnight) disappears on the dark ground, so buttons lift it while white text stays readable.
  const accent = toward(C.a, BLACK, (c) => contrastRatio(c, WHITE) >= 4.6);
  let lit = toward(accent, WHITE, (c) => contrastRatio(c, night) >= 2.4);
  if (contrastRatio(lit, WHITE) < 4.5) lit = toward(lit, BLACK, (c) => contrastRatio(c, WHITE) >= 4.5);
  const hi = toward(lit, WHITE, (c) => contrastRatio(c, lit) >= 1.12);
  const hiSafe = contrastRatio(hi, WHITE) >= 4.5 ? hi : lit;
  const atext = toward(C.a, WHITE, (c) => contrastRatio(c, n2) >= 5);
  const brass = toward(C.b, WHITE, (c) => contrastRatio(c, n2) >= 5.2);
  const ink = rgb(C.t);
  return {
    "--night": night,
    "--night-rgb": rgb(night),
    "--night-1": C.d,
    "--night-2": n2,
    "--night-3": n3,
    "--night-4": n4,
    "--ink": C.t,
    "--ink-rgb": ink,
    "--ink-2": `rgba(${ink},.8)`,
    "--ink-3": `rgba(${ink},.64)`,
    "--ink-4": `rgba(${ink},.44)`,
    "--line": `rgba(${ink},.1)`,
    "--line-2": `rgba(${ink},.19)`,
    "--line-3": `rgba(${ink},.32)`,
    "--fill": `rgba(${ink},.055)`,
    "--fill-2": `rgba(${ink},.1)`,
    "--fill-3": `rgba(${ink},.16)`,
    "--glass": `rgba(${rgb(night)},.56)`,
    "--glass-2": `rgba(${rgb(mixHex(C.d, C.a, 0.05))},.88)`,
    "--accent": accent,
    "--accent-lit": lit,
    "--accent-hi": hiSafe,
    "--accent-text": atext,
    "--accent-glow": `rgba(${rgb(lit)},.5)`,
    "--accent-wash": `rgba(${rgb(lit)},.22)`,
    "--brass": brass,
    "--brass-wash": `rgba(${rgb(brass)},.14)`,
    "--paper": C.t,
    "--paper-2": mixHex(C.t, C.i, 0.07),
    "--paper-ink": C.i,
    "--paper-ink-2": mixHex(C.i, C.t, 0.38),
    "--paper-line": `rgba(${rgb(C.i)},.15)`,
  };
}
